/* eslint-disable import/no-cycle */
import { HStack, Stack, Text, Icon, Box } from '@chakra-ui/core';
import React, { useMemo, useEffect } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import isNil from 'lodash/isNil';
import { QuestionnaireTemplateStore } from '../../../../store/questionnaireTemplateModel';
import type { OptionSelectionCount } from './questionValidationRules.types';
import { ValidationOperator } from './questionValidationRules.types';
import { VALIDATION_CONSTRAINTS } from './questionValidationRules.constraints';
import { getValidationErrorMessage, operatorRequiresMinCount, shouldChangeOperator } from './questionValidationRules.utils';
import { QuestionValidationOptionSelectMenu } from './QuestionValidationOptionSelectMenu';
import { QuestionValidationMinCountSelect } from './QuestionValidationMinCountSelect';
import { QuestionValidationSelectionCountSelect } from './QuestionValidationSelectionCountSelect';
import { QuestionValidationOperatorSelect } from './QuestionValidationOperatorSelect';

const CONTAINER_PROPS = {
  px: 4,
  py: 2,
  borderRadius: 'md',
  border: '1px solid #AED0FF',
  bg: '#EFF6FF',
} as const;

interface QuestionValidationRulesProps {
  elementId: string;
}

export const QuestionValidationRules = React.memo<QuestionValidationRulesProps>(function QuestionValidationRules({ elementId }) {
  const { t } = useTranslation('questionnaireTemplate');

  const options = QuestionnaireTemplateStore.useStoreState((s) => s.entities.elements[elementId]?.options);

  const operator = QuestionnaireTemplateStore.useStoreState(
    (s) => s.entities.elements[elementId]?.correctAnswerIf ?? /* istanbul ignore next */ ValidationOperator.IS_EQUAL,
  );

  const selectionCount = QuestionnaireTemplateStore.useStoreState(
    (s) => s.entities.elements[elementId]?.totalAnswer ?? /* istanbul ignore next */ VALIDATION_CONSTRAINTS.minSelectedOptions,
  );

  const minCount = QuestionnaireTemplateStore.useStoreState((s) => s.entities.elements[elementId]?.shouldMatchAtLeast);

  const optionsLength = options?.length ?? /* istanbul ignore next */ 0;

  const updateField = QuestionnaireTemplateStore.useStoreActions((a) => a.updateElementValue);
  const updatePreferred = QuestionnaireTemplateStore.useStoreActions((a) => a.updateOptionIsPreferred);
  const setFieldError = QuestionnaireTemplateStore.useStoreActions((a) => a.setFieldError);
  const clearFieldError = QuestionnaireTemplateStore.useStoreActions((a) => a.clearFieldError);

  const errorKey = `${elementId}.validationRule`;

  const selectedOptionIds = useMemo(
    () => options?.filter((o) => o.isPreferredAnswer).map((o) => o.id) ?? [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [options],
  );

  useEffect(() => {
    if (optionsLength <= selectionCount && operator === ValidationOperator.DOES_NOT_HAVE_ANSWER) {
      updateField({ id: elementId, path: 'totalAnswer', value: optionsLength - 1 ?? /* istanbul ignore next */ 1 });
    }
    if (optionsLength < selectionCount && operator !== ValidationOperator.DOES_NOT_HAVE_ANSWER) {
      updateField({ id: elementId, path: 'totalAnswer', value: 1 });
    }

    if (shouldChangeOperator(operator, optionsLength, t)) {
      updateField({ id: elementId, path: 'correctAnswerIf', value: ValidationOperator.IS_EQUAL });
      updateField({ id: elementId, path: 'shouldMatchAtLeast', value: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsLength, operator, selectionCount]);

  const handleSelectionCountChange = (newCount: OptionSelectionCount) => {
    updateField({ id: elementId, path: 'totalAnswer', value: newCount });
    if (operator === ValidationOperator.AT_LEAST && (minCount ?? /* istanbul ignore next */ 0) > newCount) {
      updateField({ id: elementId, path: 'shouldMatchAtLeast', value: newCount });
    }

    if (shouldChangeOperator(operator, newCount, t)) {
      updateField({ id: elementId, path: 'correctAnswerIf', value: ValidationOperator.IS_EQUAL });
      updateField({ id: elementId, path: 'shouldMatchAtLeast', value: undefined });
    }

    updatePreferred({ elementId, optionIds: [], value: true });
  };

  const handleOperatorChange = (newOperator: ValidationOperator) => {
    updateField({ id: elementId, path: 'correctAnswerIf', value: newOperator });

    const requiresMin = operatorRequiresMinCount(newOperator, t);
    updateField({
      id: elementId,
      path: 'shouldMatchAtLeast',
      value: /* istanbul ignore next */ requiresMin ? minCount ?? VALIDATION_CONSTRAINTS.minSelectedOptions : undefined,
    });
    updatePreferred({ elementId, optionIds: [], value: true });
  };

  const handleMinCountChange = (newMinCount: number) => {
    updateField({ id: elementId, path: 'shouldMatchAtLeast', value: newMinCount });
  };

  const handleOptionToggle = (optionId: string) => {
    const isSelected = selectedOptionIds.includes(optionId);

    const effectiveCap = (() => {
      switch (operator) {
        case ValidationOperator.AT_LEAST:
          // All options are selectable — cap is the total available
          return optionsLength;
        case ValidationOperator.DOES_NOT_HAVE_ANSWER:
          // Must leave at least one option unblocked
          return VALIDATION_CONSTRAINTS.DOES_NOT_HAVE_ANSWER.maxAllowed(selectionCount, optionsLength);
        case ValidationOperator.HAS_ANSWER:
          return VALIDATION_CONSTRAINTS.HAS_ANSWER.maxAllowed(selectionCount, optionsLength);
        case ValidationOperator.IS_EQUAL:
        default:
          return VALIDATION_CONSTRAINTS.IS_EQUAL.maxAllowed(selectionCount, optionsLength);
      }
    })();

    let newIds: string[];

    if (isSelected) {
      newIds = selectedOptionIds.filter((id) => id !== optionId);
    } else if (selectedOptionIds.length < effectiveCap) {
      newIds = [...selectedOptionIds, optionId];
    } else {
      newIds = effectiveCap === 1 ? [optionId] : [...selectedOptionIds.slice(1), optionId];
      // Cap reached: replace oldest selection (or swap if cap is 1)
    }

    updatePreferred({ elementId, optionIds: newIds, value: true });
  };

  const errorMessage = useMemo(
    () =>
      getValidationErrorMessage(
        { selectionCount, operator, minCount, selectedOptionIds },
        options?.length ?? /* istanbul ignore next */ 0,
        t,
      ),
    [selectionCount, operator, minCount, selectedOptionIds, options, t],
  );

  useEffect(() => {
    if (errorMessage) {
      setFieldError({ key: errorKey, error: { message: errorMessage } });
    } else {
      clearFieldError(errorKey);
    }
  }, [errorMessage, errorKey, setFieldError, clearFieldError]);

  useEffect(
    () => () => {
      clearFieldError(errorKey);
    },
    [clearFieldError, errorKey],
  );

  if (optionsLength === 0) return null;

  const ruleText2Count = selectionCount === 'any' ? 2 : selectionCount;

  return (
    <Stack {...CONTAINER_PROPS} spacing={1}>
      <HStack spacing={2}>
        <Icon as={MdInfoOutline} color="#1F3CBA" boxSize={4} />
        <Text fontSize="sm" color="#2339A0">
          {t('formBuilder.canvas.element.validationRules.title')}
        </Text>
      </HStack>

      <Box display="inline-block" lineHeight="2.5rem">
        <Text as="span" fontSize="sm" color="gray.700">
          {t('formBuilder.canvas.element.validationRules.ruleText1')}
        </Text>

        <QuestionValidationSelectionCountSelect
          elementId={elementId}
          selectionCount={selectionCount}
          operator={operator}
          onChange={handleSelectionCountChange}
        />

        <Text as="span" fontSize="sm" color="gray.700">
          {t('formBuilder.canvas.element.validationRules.ruleText2', { count: ruleText2Count })}
        </Text>

        <QuestionValidationOperatorSelect operator={operator} selectionCount={selectionCount} onChange={handleOperatorChange} />

        <QuestionValidationMinCountSelect
          elementId={elementId}
          operator={operator}
          selectionCount={selectionCount}
          minCount={minCount}
          onChange={handleMinCountChange}
        />

        <QuestionValidationOptionSelectMenu
          elementId={elementId}
          selectedOptionIds={selectedOptionIds}
          selectionCount={selectionCount}
          operator={operator}
          onToggle={handleOptionToggle}
          errorMessage={errorMessage}
        />
      </Box>

      {!isNil(errorMessage) && errorMessage !== '' && (
        <Text fontSize="xs" color="red.500" fontWeight="500" lineHeight="normal">
          {`* ${errorMessage}`}
        </Text>
      )}
    </Stack>
  );
});
